import React, { useState } from 'react';
import AdminSidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { db } from '../config/firebase';
import { collection, getDocs, query, orderBy, where, Timestamp } from 'firebase/firestore';
import { generatePDF } from '../utils/pdfGenerator';

function Reports() {
    const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();
    const [loading, setLoading] = useState(false);
    const [timeRange, setTimeRange] = useState('all');

    const getStartDate = () => {
        const now = new Date();
        switch (timeRange) {
            case '24h':
                return new Date(now.getTime() - 24 * 60 * 60 * 1000);
            case '7d':
                return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            case '30d':
                return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            default:
                return null;
        }
    };

    const generatePartnersReport = async () => {
        setLoading(true);
        try {
            let q;
            const startDate = getStartDate();

            if (startDate) {
                q = query(
                    collection(db, 'partners'),
                    where('createdAt', '>=', Timestamp.fromDate(startDate)),
                    orderBy('createdAt', 'desc')
                );
            } else {
                q = query(collection(db, 'partners'), orderBy('createdAt', 'desc'));
            }

            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => {
                const d = doc.data();
                return [
                    d.name || 'N/A',
                    d.company || 'N/A',
                    d.email || 'N/A',
                    d.phone || 'N/A',
                    d.location || 'N/A',
                    d.status || 'Active',
                    d.createdAt?.toDate ? d.createdAt.toDate().toLocaleDateString() : ''
                ];
            });

            if (data.length === 0) {
                alert("No data available for the selected period.");
                setLoading(false);
                return;
            }

            const columns = ["Name", "Company", "Email", "Phone", "Location", "Status", "Joined"];
            generatePDF(
                "Partners Report",
                `Time Range: ${timeRange === 'all' ? 'All Time' : timeRange}`,
                columns,
                data,
                `partners_report_${timeRange}.pdf`
            );

        } catch (error) {
            console.error("Error generating partners report:", error);
            alert("Failed to generate report. Check console for details.");
        } finally {
            setLoading(false);
        }
    };

    const generateEmployeesReport = async () => {
        setLoading(true);
        try {
            let q;
            const startDate = getStartDate();

            if (startDate) {
                q = query(
                    collection(db, 'employees'),
                    where('createdAt', '>=', Timestamp.fromDate(startDate)),
                    orderBy('createdAt', 'desc')
                );
            } else {
                q = query(collection(db, 'employees'), orderBy('createdAt', 'desc'));
            }

            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => {
                const d = doc.data();
                return [
                    d.name || 'N/A',
                    d.role || 'N/A',
                    d.department || 'N/A',
                    d.email || 'N/A',
                    d.phone || 'N/A',
                    d.status || 'Active',
                    d.createdAt?.toDate ? d.createdAt.toDate().toLocaleDateString() : ''
                ];
            });

            if (data.length === 0) {
                alert("No data available for the selected period.");
                setLoading(false);
                return;
            }

            const columns = ["Name", "Role", "Department", "Email", "Phone", "Status", "Joined"];
            generatePDF(
                "Employees Report",
                `Time Range: ${timeRange === 'all' ? 'All Time' : timeRange}`,
                columns,
                data,
                `employees_report_${timeRange}.pdf`
            );

        } catch (error) {
            console.error("Error generating employees report:", error);
            alert("Failed to generate report.");
        } finally {
            setLoading(false);
        }
    };

    const generateQueriesReport = async () => {
        setLoading(true);
        try {
            let q;
            const startDate = getStartDate();

            if (startDate) {
                q = query(
                    collection(db, 'queries'),
                    where('createdAt', '>=', Timestamp.fromDate(startDate)),
                    orderBy('createdAt', 'desc')
                );
            } else {
                q = query(collection(db, 'queries'), orderBy('createdAt', 'desc'));
            }

            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => {
                const d = doc.data();

                // Helper for date formatting
                const fmtDate = (ts) => ts?.toDate ? ts.toDate().toLocaleDateString() : '';

                return [
                    d.invoiceId || d.id.substring(0, 6) + '...',
                    d.title || 'N/A',
                    d.status || 'Pending',
                    d.partnerName || 'N/A',
                    d.driverName || 'Unassigned',
                    d.technicianName || 'Unassigned',
                    d.location || 'N/A',
                    fmtDate(d.startDate) || '-',
                    fmtDate(d.completedAt) || '-',
                    d.paymentMethod || '-',
                    fmtDate(d.paymentConfirmedAt) || '-'
                ];
            });

            if (data.length === 0) {
                alert("No data available for the selected period.");
                setLoading(false);
                return;
            }

            const columns = [
                "Inv #", "Title", "Status", "Partner", "Driver", "Technician",
                "Location", "Start", "End", "Pay Method", "Paid Date"
            ];

            generatePDF(
                "Queries Report",
                `Time Range: ${timeRange === 'all' ? 'All Time' : timeRange}`,
                columns,
                data,
                `queries_report_${timeRange}.pdf`,
                "Nujum Admin",
                "Administrator"
            );

        } catch (error) {
            console.error("Error generating queries report:", error);
            alert("Failed to generate report.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex h-screen overflow-hidden bg-gray-50">
            <AdminSidebar />
            <div
                className="flex-1 overflow-y-auto transition-all duration-300"
                style={{ marginLeft: isMobile ? 0 : (isOpen ? '16rem' : '5rem') }}
            >
                <div className="p-8">
                    {isMobile && (
                        <button
                            onClick={toggleMobileSidebar}
                            className="mb-4 text-gray-600 focus:outline-none"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                    )}
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">Reports Center</h1>
                    <p className="text-gray-600 mb-8">Generate and export system data (PDF).</p>

                    {/* Time Range Filter */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-8 max-w-md">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Select Reporting Period</label>
                        <select
                            value={timeRange}
                            onChange={(e) => setTimeRange(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-golden focus:border-transparent"
                        >
                            <option value="all">All Time</option>
                            <option value="24h">Last 24 Hours</option>
                            <option value="7d">Last 7 Days (Weekly)</option>
                            <option value="30d">Last 30 Days (Monthly)</option>
                        </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                        {/* Partners Report Card */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg text-gray-800">Partners Report</h3>
                                    <p className="text-sm text-gray-500">Export partner details</p>
                                </div>
                            </div>
                            <button
                                onClick={generatePartnersReport}
                                disabled={loading}
                                className="w-full py-2 px-4 bg-brand-golden text-black font-medium rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
                            >
                                {loading ? 'Generating...' : 'Download PDF'}
                            </button>
                        </div>

                        {/* Employees Report Card */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg text-gray-800">Employees Report</h3>
                                    <p className="text-sm text-gray-500">Export employee data</p>
                                </div>
                            </div>
                            <button
                                onClick={generateEmployeesReport}
                                disabled={loading}
                                className="w-full py-2 px-4 bg-gray-800 text-white font-medium rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                            >
                                {loading ? 'Generating...' : 'Download PDF'}
                            </button>
                        </div>

                        {/* Queries Report Card */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg text-gray-800">Queries Report</h3>
                                    <p className="text-sm text-gray-500">Export query history</p>
                                </div>
                            </div>
                            <button
                                onClick={generateQueriesReport}
                                disabled={loading}
                                className="w-full py-2 px-4 bg-gray-100 text-gray-800 font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                            >
                                {loading ? 'Generating...' : 'Download PDF'}
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}

export default Reports;
